import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { TransferAccountSummaryDto } from '@/features/public/bookingSummary/types/BookingPublicSummaryDto.ts';
import { getCurrencyLabel } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import { toDisplayFormat } from '@/features/dashboard/paymentConfig/types/BankAccountDto.utils.ts';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import { Description } from '@/components/ui/Title.tsx';
import { useIsMobile } from '@/hooks/use-mobile.tsx';

interface TransferAccountDetailsProps {
  transferAccounts: TransferAccountSummaryDto[];
  transactionRef: string;
}

const CopyField = ({ value, label }: { value: string; label: string }) => {
  const [copied, setCopied] = useState(false);
  const isMobile = useIsMobile();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const icon = copied ? <Check /> : <Copy />;
  const caption = copied ? 'Skopiowano' : 'Kopiuj';

  return (
    <div className="flex items-center gap-1">
      {/* Na telefonie numer konta się zawija: obcięty nie da się sprawdzić z wyciągiem. */}
      <span className="min-w-0 flex-1 break-words rounded-md bg-neutral-50 px-3 py-2 text-sm text-neutral-800 md:truncate">
        {label}
      </span>
      {/* Na telefonie napis zabierałby miejsce numerowi konta — zostaje sam znak. */}
      {isMobile ? (
        <ActionButton
          tone="outline"
          size="sm"
          iconOnly
          aria-label={caption}
          onClick={handleCopy}
        >
          {icon}
        </ActionButton>
      ) : (
        <ActionButton tone="outline" size="xs" onClick={handleCopy}>
          {icon}
          {caption}
        </ActionButton>
      )}
    </div>
  );
};

const TransferAccountDetails = ({
  transferAccounts,
  transactionRef,
}: TransferAccountDetailsProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Dane do przelewu</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Description>Tytuł przelewu</Description>
          <CopyField value={transactionRef} label={transactionRef} />
        </div>

        {transferAccounts.map((account) => (
          <div
            key={`${account.currency}-${account.accountNumber}`}
            className="space-y-2 rounded-md border border-neutral-100 p-3"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{account.bankName}</span>
              <span className="rounded-sm bg-neutral-100 px-1.5 py-0.5 text-xs font-medium text-neutral-600">
                {getCurrencyLabel(account.currency)}
              </span>
            </div>

            <div className="space-y-1">
              <Description>Numer konta</Description>
              <CopyField
                value={account.accountNumber}
                label={toDisplayFormat(
                  account.accountNumber,
                  account.accountType,
                )}
              />
            </div>

            {account.swiftCode && (
              <div className="space-y-0.5">
                <Description>SWIFT</Description>
                <p className="text-sm text-neutral-800">{account.swiftCode}</p>
              </div>
            )}

            {account.companyAddress && (
              <div className="space-y-0.5">
                <Description>Dodatkowe informacje</Description>
                <p className="text-sm text-neutral-800">
                  {account.companyAddress}
                </p>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default TransferAccountDetails;
